import { Icon } from "@blueprintjs/core";
import React, { useState } from "react";
import Toaster, { ToasterMessage } from "./Toaster";

interface ToasterContainerProps {
  toasters: ToasterMessage[];
  onRemove: (id: string) => void;
  maxVisible?: number;
}

const ToasterContainer: React.FC<ToasterContainerProps> = ({
  toasters,
  onRemove,
  maxVisible = 3,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const shouldPaginate = toasters.length > maxVisible;
  const totalPages = Math.ceil(toasters.length / maxVisible);

  const visibleToasters = shouldPaginate
    ? toasters.slice(currentPage * maxVisible, (currentPage + 1) * maxVisible)
    : toasters;

  // Reset page if current page is now out of bounds
  React.useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [currentPage, totalPages]);

  if (toasters.length === 0) return null;

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  const removeAllToasters = () => {
    toasters.forEach((toaster) => onRemove(toaster.id));
  };

  return (
    <div className="toaster-container">
      {shouldPaginate && (
        <div className="toaster-container__header">
          <div className="toaster-container__info">
            <span className="toaster-container__count">
              {toasters.length} notification{toasters.length !== 1 ? "s" : ""}
            </span>
            {totalPages > 1 && (
              <span className="toaster-container__page">
                Page {currentPage + 1} of {totalPages}
              </span>
            )}
          </div>
          <div className="toaster-container__controls">
            {totalPages > 1 && (
              <>
                <button
                  className="toaster-container__nav-btn"
                  onClick={handlePrevPage}
                  disabled={currentPage === 0}
                  title="Previous page"
                >
                  <Icon icon="chevron-up" size={14} />
                </button>
                <button
                  className="toaster-container__nav-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages - 1}
                  title="Next page"
                >
                  <Icon icon="chevron-down" size={14} />
                </button>
              </>
            )}
            <button
              className="toaster-container__clear-btn"
              onClick={removeAllToasters}
              title="Clear all notifications"
            >
              <Icon icon="cross" size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="toaster-container__list">
        {visibleToasters.map((toaster, index) => (
          <Toaster
            key={toaster.id}
            message={toaster}
            onRemove={onRemove}
            style={
              {
                "--toast-index": index,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
};

export default ToasterContainer;
